import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { showToast } from './ToastContainer';
import '../styles/ReviewModal.css';

const POSITIVE_TAGS = [
  'Good price',
  'Fast delivery',
  'GOAT!',
  'Account as described',
  'Went extra mile!',
  'Helpful and responsive',
  'Good communication',
];

const ReviewModal = ({ isOpen, onClose, order, onSubmit, existingReview = null }) => {
  const [thumbsUp, setThumbsUp] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (existingReview) {
      const nextComment = existingReview.comment || '';
      setThumbsUp(typeof existingReview.thumbs_up === 'boolean' ? existingReview.thumbs_up : null);
      setComment(nextComment);
      setSelectedTags(POSITIVE_TAGS.filter((tag) => nextComment.includes(tag)));
    } else {
      setThumbsUp(null);
      setComment('');
      setSelectedTags([]);
    }
  }, [isOpen, existingReview]);

  if (!isOpen || !order) return null;

  const sellerName = order.seller?.username || 'Seller';
  const orderLabel = `#${String(order.id).slice(0, 8).toUpperCase()}`;
  const canSubmit = thumbsUp !== null && Boolean(comment.trim() || selectedTags.length);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (thumbsUp === null) {
      showToast('Choose thumbs up or thumbs down.', 'error');
      return;
    }

    const finalComment = comment.trim() || selectedTags.join(', ');
    if (!finalComment) {
      showToast('Add a tag or write a short comment.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        orderId: order.id,
        sellerId: order.seller_id,
        thumbsUp,
        comment: finalComment,
        tags: selectedTags,
      });
      setThumbsUp(null);
      setComment('');
      setSelectedTags([]);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      showToast(error.message || 'Failed to save review. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleThumbsClick = (value) => {
    setThumbsUp(value);
    if (value === false) setSelectedTags([]);
  };

  const toggleTag = (tag) => {
    if (thumbsUp === false) return;
    setSelectedTags((prev) => {
      const next = prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag];
      setComment(next.join(', '));
      return next;
    });
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div
        className="review-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="review-modal-head">
          <div>
            <span className="review-modal-kicker">Review</span>
            <h2 id="review-modal-title">{existingReview ? 'Update your review' : 'Leave a review'}</h2>
          </div>
          <button type="button" className="review-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="review-modal-form">
          <dl className="review-modal-meta">
            <div>
              <dt>Order</dt>
              <dd>{orderLabel}</dd>
            </div>
            <div>
              <dt>Seller</dt>
              <dd>{sellerName}</dd>
            </div>
          </dl>

          <fieldset className="review-modal-field">
            <legend>How did it go?</legend>
            <div className="review-thumbs">
              <button
                type="button"
                className={`review-thumb${thumbsUp === true ? ' is-up' : ''}`}
                onClick={() => handleThumbsClick(true)}
              >
                <ThumbsUp size={18} strokeWidth={2.2} />
                <span>Thumbs up</span>
              </button>
              <button
                type="button"
                className={`review-thumb${thumbsUp === false ? ' is-down' : ''}`}
                onClick={() => handleThumbsClick(false)}
              >
                <ThumbsDown size={18} strokeWidth={2.2} />
                <span>Thumbs down</span>
              </button>
            </div>
          </fieldset>

          {thumbsUp === true && (
            <fieldset className="review-modal-field">
              <legend>What went well?</legend>
              <div className="review-tags">
                {POSITIVE_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`review-tag${selectedTags.includes(tag) ? ' is-on' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          <label className="review-modal-field" htmlFor="review-comment">
            <span>
              Comment
              {selectedTags.length === 0 ? ' — required' : ' — optional'}
            </span>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={
                selectedTags.length > 0
                  ? 'Add more detail if you want.'
                  : 'Write a short comment, or pick a tag above.'
              }
              rows={4}
              maxLength={500}
            />
            <em>{comment.length}/500</em>
          </label>

          <div className="review-modal-actions">
            <button type="button" className="review-btn review-btn--ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="review-btn review-btn--primary" disabled={submitting || !canSubmit}>
              {submitting ? 'Saving…' : existingReview ? 'Save review' : 'Leave review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;
